/**
 * HROnboarding.tsx — HR / Onboarding (ON-2: the first UI caller of the ON-1
 * routes, tables 0106).
 *
 * The mockup's view (klaxiora-crm-mockup-full.html :2064-2082) is a head +
 * "Start onboarding" CTA over an empty-state card — its copy is kept
 * verbatim for the no-checklists state. Everything beyond that is the ON-1
 * slice definition: a checklists section (per-hire instances, tick/untick,
 * fenced delete) and a template manager. Start-onboarding is a centered
 * MODAL (the CreateExpenseModal / CreateEmployeeModal idiom); template
 * create/edit is the house right-side drawer (the SopDrawer precedent) with
 * one item per line — the server assigns positions from line order.
 *
 * Progress figures render the SERVER's doneCount/itemCount verbatim
 * (onboardingView.ts) — the pin-3 derivation is never recomputed here.
 * Tick/untick swap in the instance the server returns; start/delete/template
 * writes re-fetch — the server row is authoritative (the SOPs posture).
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, Plus, X } from 'lucide-react'
import { toast } from 'sonner'
import { SectionTitle, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import {
  useAsync, fetchDirectory,
  fetchOnboarding, startOnboarding, completeOnboardingItem, uncompleteOnboardingItem, deleteOnboarding,
  fetchOnboardingTemplates, createOnboardingTemplate, updateOnboardingTemplate, deleteOnboardingTemplate,
} from '../../api'
import { apiErrorMessage } from '../../../../api/client'
import { overlay } from '../../../../theme/tokens'
import type { Onboarding, OnboardingTemplate } from '../../types'
import {
  progressLine, progressPct, isOnboardingComplete, onboardingCountLine, onboardingMeta,
  templateMeta, linesToItems, itemsToLines,
} from './onboardingView'

const inputCls = 'w-full px-4 py-3 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body placeholder:text-ink-faint transition-all'
const labelCls = 'block text-12 font-semibold text-ink-body mb-2'

// ── Start-onboarding modal (CreateExpenseModal idiom) ─────────────────────────

function StartOnboardingModal({ templates, onClose, onStarted }: {
  templates: OnboardingTemplate[]
  onClose: () => void
  onStarted: () => void
}) {
  const [employeeId, setEmployeeId] = useState('')
  const [templateId, setTemplateId] = useState(templates.length === 1 ? templates[0].id : '')
  const [busy, setBusy] = useState(false)
  // Active employees only — a leaver does not onboard (the ON-1 battery's
  // own fixture lesson).
  const employees = useAsync(() => fetchDirectory({ status: 'active' }), [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  async function submit() {
    setBusy(true)
    try {
      await startOnboarding(employeeId, templateId)
      toast.success('Onboarding started')
      onStarted()
      onClose()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Starting the onboarding failed'))
    } finally {
      setBusy(false)
    }
  }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      className="flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div role="dialog" aria-modal="true" aria-label="Start onboarding"
        className="k-card w-full max-w-md p-6 gap-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-detail-title font-bold text-ink-primary">Start onboarding</div>
            <div className="text-12 text-ink-muted mt-0.5">The checklist snapshots the template — later template edits never touch it.</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors flex-shrink-0">
            <X size={16} />
          </button>
        </div>

        <div className="flex flex-col gap-3">
          <div>
            <label htmlFor="start-onb-employee" className={labelCls}>Employee</label>
            {employees.error ? (
              <div className="text-12 text-ink-muted px-1 py-3">Couldn't load employees</div>
            ) : (
              <select id="start-onb-employee" value={employeeId} onChange={e => setEmployeeId(e.target.value)}
                disabled={employees.loading} className={inputCls}>
                <option value="" disabled>Select…</option>
                {(employees.data ?? []).map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.fullName}</option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label htmlFor="start-onb-template" className={labelCls}>Template</label>
            <select id="start-onb-template" value={templateId} onChange={e => setTemplateId(e.target.value)}
              disabled={templates.length === 0} className={inputCls}>
              <option value="" disabled>{templates.length === 0 ? 'No templates yet' : 'Select…'}</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.title}</option>
              ))}
            </select>
            {templates.length === 0 && (
              <p className="text-12 text-ink-muted mt-2 m-0">Create a template below first — a checklist starts from one.</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={submit} disabled={busy || !employeeId || !templateId}
            className="k-btn k-btn--lg flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
            <Check size={14} /> {busy ? 'Starting…' : 'Start onboarding'}
          </button>
          <button onClick={onClose} disabled={busy}
            className="k-btn-secondary flex-1 disabled:opacity-40">
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── Template create / edit drawer (SopDrawer precedent) ───────────────────────

function TemplateDrawer({ template, onClose, onSaved }: {
  template: OnboardingTemplate | null // null = create
  onClose: () => void
  onSaved: () => void
}) {
  const [title, setTitle] = useState(template?.title ?? '')
  const [itemsText, setItemsText] = useState(template ? itemsToLines(template.items) : '')

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  async function handleSave() {
    const items = linesToItems(itemsText)
    if (!title.trim()) { toast.error('Title is required'); return }
    if (items.length === 0) { toast.error('At least one item is required'); return }
    const write = { title: title.trim(), items }
    try {
      if (template) {
        await updateOnboardingTemplate(template.id, write)
        toast.success('Template updated')
      } else {
        await createOnboardingTemplate(write)
        toast.success('Template created')
      }
      onSaved()
      onClose()
    } catch (e) {
      toast.error(apiErrorMessage(e, template ? 'Failed to update template' : 'Failed to create template'))
    }
  }

  async function handleDelete() {
    if (!template) return
    try {
      await deleteOnboardingTemplate(template.id)
      toast.success('Template deleted')
      onSaved()
      onClose()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to delete template'))
    }
  }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="fixed top-0 right-0 bg-surface-card border-l border-edge-row flex flex-col"
        style={{ height: '100dvh', width: 'min(580px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>

        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div>
            <div className="text-detail-title font-bold text-ink-primary">{template ? 'Edit Template' : 'New Template'}</div>
            <div className="text-12 text-ink-muted mt-1">
              {template
                ? 'Edits apply to future starts only — checklists already running keep their snapshot.'
                : 'A reusable checklist every new hire starts from.'}
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
          <div>
            <label className={labelCls}>Title<span className="text-danger ml-1">*</span></label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. New Hire Checklist" autoFocus className={inputCls} />
          </div>

          <div>
            <label className={labelCls}>Items<span className="text-danger ml-1">*</span></label>
            <textarea value={itemsText} onChange={e => setItemsText(e.target.value)}
              placeholder={'Sign the contract\nSet up laptop and accounts\nBook the induction call'} rows={10}
              className={`${inputCls} resize-none`} />
            <p className="text-12 text-ink-muted mt-2 m-0">One item per line — the order here is the checklist order.</p>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 px-6 pt-5 pb-14 border-t border-edge-row flex-shrink-0">
          {template ? (
            <button onClick={handleDelete} className="k-btn-secondary text-danger">
              Delete
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="k-btn-secondary">
              Cancel
            </button>
            <button onClick={handleSave} className="k-btn">
              {template ? 'Save Changes' : 'Save Template'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── Checklist card ────────────────────────────────────────────────────────────

function ChecklistCard({ onboarding, onPatched, onRemoved }: {
  onboarding: Onboarding
  onPatched: (o: Onboarding) => void
  onRemoved: () => void
}) {
  const [busyItem, setBusyItem] = useState<string | null>(null)
  const complete = isOnboardingComplete(onboarding)

  async function toggle(itemId: string, done: boolean) {
    setBusyItem(itemId)
    try {
      const refreshed = done ? await uncompleteOnboardingItem(itemId) : await completeOnboardingItem(itemId)
      onPatched(refreshed)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Updating the item failed'))
    } finally {
      setBusyItem(null)
    }
  }

  async function remove() {
    try {
      await deleteOnboarding(onboarding.id)
      toast.success('Checklist removed')
      onRemoved()
    } catch (e) {
      // The completed-checklist fence is the server's named 400 (pin 5).
      toast.error(apiErrorMessage(e, 'Removing the checklist failed'))
    }
  }

  return (
    <div className="closr-card px-5 py-4 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-13 font-semibold text-ink-primary">{onboarding.employeeName || '—'}</div>
          <div className="text-11 text-ink-faint mt-1">{onboardingMeta(onboarding)}</div>
        </div>
        <span className={`text-11 font-medium whitespace-nowrap ${complete ? 'text-success' : 'text-ink-muted'}`}>
          {complete ? 'Complete' : progressLine(onboarding)}
        </span>
      </div>

      <div className="h-1 rounded-full bg-track overflow-hidden">
        <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${progressPct(onboarding)}%` }} />
      </div>

      <ul className="flex flex-col gap-1 m-0 p-0 list-none">
        {onboarding.items.map(item => {
          const done = item.status === 'done'
          return (
            <li key={item.id}>
              <button onClick={() => toggle(item.id, done)} disabled={busyItem === item.id}
                className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-control text-left hover:bg-surface-row-hover transition-colors disabled:opacity-50"
                aria-pressed={done}>
                <span className={`w-4 h-4 flex-shrink-0 rounded-[4px] border flex items-center justify-center transition-colors ${done ? 'bg-accent border-accent' : 'border-edge-control'}`}>
                  {done && <Check size={11} strokeWidth={3} className="text-surface-card" />}
                </span>
                <span className={`text-12 ${done ? 'text-ink-faint line-through' : 'text-ink-body'}`}>{item.title}</span>
              </button>
            </li>
          )
        })}
      </ul>

      {!complete && (
        <div className="flex justify-end">
          <button onClick={remove} className="k-btn-quiet text-danger">Remove</button>
        </div>
      )}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function HROnboarding() {
  const [reloadKey, setReloadKey] = useState(0)
  const [startOpen, setStartOpen] = useState(false)
  const [drawer, setDrawer] = useState<{ open: boolean; template: OnboardingTemplate | null }>({ open: false, template: null })
  // Tick/untick responses are swapped in per-id; a reload clears the patches
  // (the refetched list is authoritative).
  const [patched, setPatched] = useState<Record<string, Onboarding>>({})

  const instancesQ = useAsync(fetchOnboarding, [reloadKey])
  const templatesQ = useAsync(fetchOnboardingTemplates, [reloadKey])

  if (instancesQ.loading || templatesQ.loading) return <LoadingState />
  if (instancesQ.error) return <ErrorState message={instancesQ.error} />
  if (templatesQ.error) return <ErrorState message={templatesQ.error} />

  const instances = (instancesQ.data ?? []).map(o => patched[o.id] ?? o)
  const templates = templatesQ.data ?? []

  const reload = () => { setPatched({}); setReloadKey(k => k + 1) }

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle
        title="Onboarding"
        subtitle={instances.length === 0 ? 'New hire onboarding checklists.' : onboardingCountLine(instances)}
        action={
          <button className="k-btn inline-flex items-center gap-2" onClick={() => setStartOpen(true)}>
            <Plus size={14} /> Start Onboarding
          </button>
        }
      />

      {instances.length === 0 ? (
        <div className="closr-card">
          {/* The mockup's empty state, copy verbatim (:2076-2080). */}
          <EmptyState
            message="No onboarding in progress"
            hint="New hire checklists will appear here once someone joins"
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {instances.map(o => (
            <ChecklistCard key={o.id} onboarding={o}
              onPatched={refreshed => setPatched(p => ({ ...p, [refreshed.id]: refreshed }))}
              onRemoved={reload}
            />
          ))}
        </div>
      )}

      <div className="flex items-center justify-between mt-2">
        <h3 className="text-13 font-semibold text-ink-primary m-0">Templates</h3>
        <button className="k-btn-quiet inline-flex items-center gap-1.5" onClick={() => setDrawer({ open: true, template: null })}>
          <Plus size={13} /> New Template
        </button>
      </div>

      {templates.length === 0 ? (
        <div className="closr-card">
          <EmptyState
            message="No templates yet"
            hint="Create a checklist template and every new hire can start from it"
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {templates.map(t => (
            <button key={t.id} onClick={() => setDrawer({ open: true, template: t })}
              className="closr-card px-5 py-4 text-left cursor-pointer hover:bg-surface-row-hover transition-colors">
              <div className="text-13 font-semibold text-ink-primary">{t.title}</div>
              <div className="text-11 text-ink-faint mt-1">{templateMeta(t)}</div>
              <ol className="text-12 text-ink-secondary mt-3 m-0 pl-4 overflow-hidden"
                style={{ display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical' }}>
                {t.items.map(i => <li key={i.id}>{i.title}</li>)}
              </ol>
            </button>
          ))}
        </div>
      )}

      {startOpen && (
        <StartOnboardingModal templates={templates} onClose={() => setStartOpen(false)} onStarted={reload} />
      )}
      {drawer.open && (
        <TemplateDrawer template={drawer.template}
          onClose={() => setDrawer({ open: false, template: null })}
          onSaved={reload}
        />
      )}
    </div>
  )
}
